import React, { useMemo } from "react";
import VStack from "../../../ui_atoms/VStack";
import { MockedProvider } from "@apollo/client/testing";
import {
    buildClientInfo,
    buildMissionInfo,
    buildTimesheet,
    buildUser,
    buildWorkerInfo,
} from "../../factories";
import { GET_ALL_TIMESHEETS, SUBMIT_EXACT_TIME_SHEET } from "../../../graphql/timesheet";
import type { Meta, StoryObj } from "@storybook/react";
import { createStackNavigator } from "@react-navigation/stack";
import { ProfileType, TimeSheetState } from "../../../__generated__/graphql";
import TimeSheetListScreen from "../../../screens/user/TimeSheetListScreen";
import { MockedAuthManager } from "../../../contexts/AuthManager";
import { MockedProfileSwitcherManager } from "../../../contexts/ProfileSwitcherManager";

const Stack = createStackNavigator<{
    TimeSheetListScreen: { id: string };
}>();

const StoryComponent = ({ profileType }: { profileType: ProfileType }) => {
    const user = useMemo(
        () =>
            buildUser({
                workerInfo: buildWorkerInfo(),
                clientInfos: [buildClientInfo({ id: "client" })],
            }),
        [],
    );
    const missionInfo = useMemo(() => {
        return buildMissionInfo({});
    }, []);

    const mocks = [
        {
            request: {
                query: GET_ALL_TIMESHEETS,
            },
            variableMatcher: () => true,

            newData: () => ({
                data: {
                    getAllTimeSheets: {
                        count: 2,
                        elements: [
                            buildTimesheet({
                                status: TimeSheetState.Open,
                                missionInfo: missionInfo,
                            }),
                            buildTimesheet({
                                status: TimeSheetState.Submitted,
                                missionInfo: missionInfo,
                            }),
                            buildTimesheet({
                                status: TimeSheetState.Rejected,
                                missionInfo: missionInfo,
                            }),
                            buildTimesheet({
                                status: TimeSheetState.Accepted,
                                missionInfo: missionInfo,
                            }),
                        ],
                        pageInfo: {},
                    },
                },
            }),
        },
        {
            variableMatcher: () => true,

            request: {
                query: SUBMIT_EXACT_TIME_SHEET,
            },
            newData: () => {
                return {
                    data: {
                        submitExactTimeSheet: { id: "timesheet-id" },
                    },
                };
            },
        },
    ];
    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={profileType}>
                <MockedProvider mocks={mocks}>
                    <VStack alignment="stretch" style={{ flex: 1 }}>
                        <Stack.Navigator initialRouteName="TimeSheetListScreen">
                            <Stack.Screen
                                name="TimeSheetListScreen"
                                component={TimeSheetListScreen}
                            />
                        </Stack.Navigator>
                    </VStack>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};
const meta = {
    title: "Timesheet/List",
    component: StoryComponent,
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Worker: Story = {
    args: {
        profileType: ProfileType.Worker,
    },
};

export const Client: Story = {
    args: {
        profileType: ProfileType.Client,
    },
};
